import { initScrollSnapSlider, updateScrollSnapSliderControls } from './scroll_snap_slider'
import { initReformCaseShortlistDetail } from './reform_case_shortlist/detail_button'
import './reform_case_shortlist/card_button'

export function initCasePhotoGallery() {
  document.querySelectorAll('[data-reform-case-photo-gallery]').forEach((gallery) => {
    if (gallery.dataset.photoGalleryInitialized === 'true') return
    gallery.dataset.photoGalleryInitialized = 'true'

    const track = gallery.querySelector('[data-scroll-snap-slider-track]')
    const templates = gallery.querySelectorAll('template[data-reform-case-deferred-image]')
    if (!(track instanceof HTMLElement) || templates.length === 0) return

    const caption = gallery.querySelector('[data-reform-case-photo-caption]')
    if (caption instanceof HTMLElement) {
      track.addEventListener('scroll', () => {
        const step = track.children[1].offsetLeft - track.children[0].offsetLeft
        if (step <= 0) return
        const slide = track.children[Math.round(track.scrollLeft / step)]
        const text = slide?.querySelector('h3')?.textContent ?? ''
        if (caption.textContent !== text) caption.textContent = text
      }, { passive: true })
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.intersectionRatio <= 0) return
        const template = entry.target.querySelector('template[data-reform-case-deferred-image]')
        if (template instanceof HTMLTemplateElement) template.replaceWith(template.content)
        observer.unobserve(entry.target)
      })
    }, { root: track, threshold: 0.01 })

    templates.forEach((template) => observer.observe(template.parentElement))
  })
}

function refreshScrollSnapSliders(root) {
  initScrollSnapSlider(root)
  root.querySelectorAll('[data-scroll-snap-slider]').forEach((sliderRoot) => updateScrollSnapSliderControls(sliderRoot))
}

function activateSimilarCaseTab(tab) {
  const root = tab.closest('[data-similar-case-tabs]')
  const target = tab.getAttribute('data-similar-case-tab')
  if (!(root instanceof HTMLElement) || target === null) return

  root.querySelectorAll('[data-similar-case-tab]').forEach((candidate) => {
    candidate.classList.toggle('reform-case-detail__similar-tab--active', candidate === tab)
  })

  root.querySelectorAll('[data-similar-case-panel]').forEach((panel) => {
    panel.hidden = panel.getAttribute('data-similar-case-panel') !== target
  })

  const activePanel = root.querySelector(`[data-similar-case-panel="${target}"]`)
  if (activePanel instanceof HTMLElement) refreshScrollSnapSliders(activePanel)
}

export function initSimilarCaseTabs() {
  document.addEventListener('click', (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[data-similar-case-tab]') : null
    if (tab instanceof HTMLElement) activateSimilarCaseTab(tab)
  })
}

function directImageElementOf(trigger) {
  return Array.from(trigger.children).find((child) => (
    child instanceof HTMLPictureElement || child instanceof HTMLImageElement
  )) ?? null
}

export function buildMagnifiedImageHtml(trigger) {
  const imageElement = directImageElementOf(trigger)
  if (!(imageElement instanceof Element)) return null

  const sourceImage = imageElement instanceof HTMLPictureElement
    ? imageElement.querySelector('img')
    : imageElement
  if (!(sourceImage instanceof HTMLImageElement)) return null

  const magnifiedImageSrc = sourceImage.getAttribute('data-magnified-image-src')
  if (!magnifiedImageSrc) {
    return {
      html: imageElement.outerHTML,
      needsResize: true,
    }
  }

  const magnifiedImage = sourceImage.cloneNode(true)
  magnifiedImage.setAttribute('src', magnifiedImageSrc)
  ;[
    'srcset',
    'sizes',
    'data-src',
    'data-srcset',
    'data-sizes',
    'data-eio',
    'data-eio-rwidth',
    'data-eio-rheight',
    'loading',
  ].forEach((attribute) => {
    magnifiedImage.removeAttribute(attribute)
  })
  magnifiedImage.classList.remove('lazyload')

  return {
    html: magnifiedImage.outerHTML,
    needsResize: false,
  }
}

export function initMagnifiedImageModal() {
  document.addEventListener('click', (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('.magnifiable-img') : null
    if (!(trigger instanceof HTMLElement)) return

    const modal = document.getElementById('magnified-img-modal')
    if (!(modal instanceof HTMLDialogElement)) return
    const imageContent = modal.querySelector('[data-magnified-image-content]')
    if (!(imageContent instanceof HTMLElement)) return

    const magnifiedImage = buildMagnifiedImageHtml(trigger)
    if (magnifiedImage === null) return

    event.preventDefault()
    imageContent.innerHTML = magnifiedImage.html
    if (magnifiedImage.needsResize) {
      window.dispatchEvent(new Event('resize'))
    }

    modal.showModal()
  })

  document.addEventListener('click', (event) => {
    const modal = event.target instanceof Element ? event.target.closest('#magnified-img-modal') : null
    if (!(modal instanceof HTMLDialogElement)) return

    event.preventDefault()
    modal.close()
  })
}

export function initReformCaseDetail() {
  initCasePhotoGallery()
  initScrollSnapSlider(document, { snapToItemRootSelector: '[data-reform-case-photo-gallery]' })
  initSimilarCaseTabs()
  initMagnifiedImageModal()
  initReformCaseShortlistDetail()
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initReformCaseDetail)
} else {
  initReformCaseDetail()
}
